/**
 * FormationSearch — Custom Element (extends HTMLFormElement, is="formation-search")
 *
 * ── Rôle ─────────────────────────────────────────────────────────────────────
 * Pilote la recherche de formations en AJAX sur la page Liste.cshtml.
 * Il est activé en ajoutant l'attribut `is="formation-search"` sur le <form>
 * et enregistré dans main.ts via :
 *   customElements.define('formation-search', FormationSearch, { extends: 'form' });
 *
 * ── Flux général ─────────────────────────────────────────────────────────────
 *  1. L'utilisateur coche un filtre, tape dans la barre de recherche ou soumet.
 *  2. buildPayload() collecte tous les champs du <form> en un objet JSON.
 *  3. fetchResults() envoie ce payload en POST vers l'endpoint Razor partial :
 *       POST /api/recherche/formations/partial
 *       → SearchController.FormationsPartial() (CCIFormation.Front)
 *       → retourne le HTML du partial _ResultatsFormation.cshtml
 *  4. Les trois zones HTML cibles sont remplacées sans rechargement de page :
 *       #formations-count      ← compteur "N formation(s) trouvée(s)"
 *       #formations-results    ← grille des cards de formations
 *       #formations-pagination ← liens de pagination
 *  5. updateUrl() met à jour l'URL dans la barre du navigateur (history.pushState)
 *     pour permettre le partage de lien et le bouton "retour" du navigateur.
 *  6. bindPaginationLinks() re-branche les clics sur la pagination injectée.
 *
 * ── En-tête propre à un domaine (CFB-519) ────────────────────────────────────
 *  Sur /domaine/{nom}[/{sousNom}], ou sur la liste quand un seul domaine est
 *  sélectionné, l'en-tête (title, H1, bannière, fil d'Ariane, contenu CCIT) est
 *  rendu côté serveur pour ce domaine. Si l'utilisateur change de domaine dans les
 *  filtres, on recharge la liste de résultats (data-liste-url) au lieu de
 *  rafraîchir en AJAX, pour ne pas laisser un en-tête périmé. Au-delà d'un domaine,
 *  le serveur rend l'en-tête générique de la liste.
 *
 * ── Étiquettes des filtres actifs (CFB-519) ──────────────────────────────────
 *  #filtres-actifs liste chaque filtre actif avec une croix pour le retirer.
 *
 * ── Champs du formulaire attendus ────────────────────────────────────────────
 *  name="recherche"   → texte libre (barre de recherche)
 *  name="domaines"    → checkbox, valeur = GUID du domaine ou sous-domaine
 *  name="typologies"  → checkbox, valeur = slug de la typologie
 *  name="ccits"       → checkbox, valeur = slug de la CCIT
 *  name="niveaux"     → checkbox, valeur = slug du niveau d'entrée
 *  name="criteres"    → checkbox, valeur = slug du critère libre
 *  name="cpf"         → checkbox, valeur fixe "true"
 *
 * ── Zones HTML cibles (hors du <form>, dans la même page) ────────────────────
 *  #formations-count      → défini dans _ResultatsFormation.cshtml
 *  #formations-results    → défini dans _ResultatsFormation.cshtml
 *  #formations-pagination → défini dans _ResultatsFormation.cshtml
 *
 * ── Dépendances ───────────────────────────────────────────────────────────────
 *  Côté serveur : SearchController.FormationsPartial() dans CCIFormation.Front
 *  Côté HTML    : Les IDs #formations-count/results/pagination doivent exister
 *                 dans le DOM au moment du connectedCallback().
 */
export class FormationSearch extends HTMLFormElement {

    // ── Constantes ────────────────────────────────────────────────────
    //
    // ENDPOINT : URL de l'endpoint Razor partial.
    //            Doit correspondre à la route définie dans SearchController.
    //
    // DEBOUNCE_MS : délai (ms) avant déclenchement de la recherche lors de la
    //               saisie dans la barre de recherche, pour éviter un appel à
    //               chaque frappe clavier.

    private static readonly ENDPOINT    = '/api/recherche/formations/partial';
    private static readonly DEBOUNCE_MS = 400;

    // ── Zones HTML cibles ─────────────────────────────────────────────
    //
    // Références aux éléments du DOM injectés après chaque réponse AJAX.
    // Initialisées dans connectedCallback() car le DOM n'est pas encore
    // disponible dans le constructeur d'un Custom Element.

    private countEl!:   HTMLElement;        // compteur de résultats
    private resultsEl!: HTMLElement;        // grille des cards
    private paginEl!:   HTMLElement;        // pagination
    private filtresActifsEl: HTMLElement | null = null; // étiquettes des filtres actifs

    /** Champ texte de la barre de recherche (peut être absent si non rendu). */
    private inputSearch: HTMLInputElement | null = null;

    /**
     * Timer de la prochaine recherche (debounce de la saisie texte, ou regroupement des
     * événements change). Annulé si un nouvel événement arrive avant expiration.
     */
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Contrôleur de la requête AJAX en cours. Permet d'annuler une requête encore
     * en vol lorsqu'un nouveau clic (pagination) ou un nouveau filtre est déclenché,
     * pour que seule la dernière demande aboutisse (navigation plus réactive).
     */
    private inFlight: AbortController | null = null;

    // ── Handlers ─────────────────────────────────────────────────────
    //
    // Les handlers sont stockés comme propriétés de classe (et non comme
    // lambdas inline) pour pouvoir être passés à removeEventListener()
    // dans disconnectedCallback() avec la même référence.

    private changeHandler!: (e: Event) => void;
    private submitHandler!: (e: SubmitEvent) => void;
    private inputHandler!:  () => void;
    private filtresActifsClickHandler!: (e: MouseEvent) => void;

    constructor() {
        super();
    }

    /**
     * Appelé par le navigateur quand l'élément est inséré dans le DOM.
     * C'est ici que les références DOM et les event listeners sont initialisés.
     */
    connectedCallback() {
        // Résolution des zones cibles (hors du <form>)
        this.countEl     = document.getElementById('formations-count')!;
        this.resultsEl   = document.getElementById('formations-results')!;
        this.paginEl     = document.getElementById('formations-pagination')!;
        this.inputSearch = this.querySelector<HTMLInputElement>('#input-recherche');
        this.filtresActifsEl = this.querySelector<HTMLElement>('#filtres-actifs');

        // Déclenché quand une checkbox change d'état.
        // La vérification `target.type === 'checkbox'` évite de réagir
        // à d'autres éléments interactifs éventuels dans le form.
        // FilterTree propage la coche d'un domaine parent en émettant un change par
        // sous-domaine : le délai nul regroupe ces événements en une seule recherche,
        // lancée une fois l'état du formulaire complet.
        this.changeHandler = (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (target.type === 'checkbox') {
                this.scheduleSearch(0); // retour à la page 1 à chaque nouveau filtre
            }
        };

        // Déclenché sur submit (bouton loupe ou touche Entrée dans la barre).
        // preventDefault() empêche le rechargement natif du navigateur.
        this.submitHandler = (e: SubmitEvent) => {
            e.preventDefault();
            this.fetchResults(1);
        };

        // Déclenché à chaque frappe dans la barre de recherche.
        // Le debounce évite un appel réseau pour chaque caractère saisi.
        this.inputHandler = () => this.scheduleSearch(FormationSearch.DEBOUNCE_MS);

        // Clic sur la croix d'une étiquette ou sur « Tout effacer ».
        this.filtresActifsClickHandler = (e: MouseEvent) => {
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-retirer]');
            if (btn) this.retirerFiltre(btn.dataset.retirer!);
        };

        this.addEventListener('change', this.changeHandler);
        this.addEventListener('submit', this.submitHandler);
        this.inputSearch?.addEventListener('input', this.inputHandler);
        this.filtresActifsEl?.addEventListener('click', this.filtresActifsClickHandler);

        // Branchement initial des liens de pagination SSR (rendu serveur)
        this.bindPaginationLinks();
        this.renderFiltresActifs();
    }

    /**
     * Appelé par le navigateur quand l'élément est retiré du DOM.
     * Nettoie tous les listeners et le timer pour éviter les fuites mémoire.
     */
    disconnectedCallback() {
        this.removeEventListener('change', this.changeHandler);
        this.removeEventListener('submit', this.submitHandler);
        this.inputSearch?.removeEventListener('input', this.inputHandler);
        this.filtresActifsEl?.removeEventListener('click', this.filtresActifsClickHandler);
        if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    }

    /**
     * Programme une recherche (page 1) après `delay` ms, en remplaçant toute recherche
     * déjà programmée : seule la dernière demande part.
     */
    private scheduleSearch(delay: number): void {
        if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.debounceTimer = null;
            this.fetchResults(1);
        }, delay);
    }

    // ── Payload ───────────────────────────────────────────────────────

    /**
     * Collecte tous les champs du <form> via FormData et construit le payload
     * JSON attendu par SearchController.FormationsPartial().
     *
     * Conventions :
     *  - Les champs multi-valeurs (checkbox) sont collectés avec getAll().
     *  - Un tableau vide est converti en `null` pour ne pas envoyer de filtre vide.
     *  - `domaines` contient des GUIDs (valeur directe de la checkbox).
     *  - Tous les autres filtres contiennent des slugs (résolus côté serveur en GUIDs).
     *
     * @param page Numéro de page (0-based) à demander.
     */
    private buildPayload(page: number): FormationSearchPayload {
        const data = new FormData(this);

        const domaines   = data.getAll('domaines')   as string[]; // IDs (int)
        const typologies = data.getAll('typologies') as string[]; // IDs (int)
        const ccits      = data.getAll('ccits')      as string[]; // IDs (int)
        const niveaux    = data.getAll('niveaux')    as string[]; // IDs (int)
        const criteres   = data.getAll('criteres')   as string[]; // IDs (int)
        const cpfVal     = data.get('cpf') as string | null;      // "true" | null
        const parentId   = this.dataset.domaineParentId;          // présent sur la route domaine parent

        return {
            recherche:    (data.get('recherche') as string | null) || null,
            localisation: (data.get('localisation') as string | null) || null,
            domaines:   domaines.length   ? domaines.map(Number)   : null,
            typologies: typologies.length ? typologies.map(Number) : null,
            ccits:      ccits.length      ? ccits.map(Number)      : null,
            niveaux:    niveaux.length    ? niveaux.map(Number)    : null,
            criteres:   criteres.length   ? criteres.map(Number)   : null,
            cpf:        cpfVal === 'true' ? true                   : null,
            page,
            domaineParentId: parentId ? Number(parentId) : null,
        };
    }

    // ── URL history ───────────────────────────────────────────────────

    /**
     * Met à jour l'URL dans la barre du navigateur sans rechargement de page,
     * en reflétant l'état courant des filtres actifs.
     *
     * Utilise history.pushState() pour :
     *  - permettre le partage d'URL avec les filtres pré-cochés,
     *  - faire fonctionner le bouton "retour" du navigateur (SSR au rechargement).
     *
     * Les filtres nuls ou vides ne sont pas ajoutés à la querystring.
     *
     * @param payload Le payload qui vient d'être envoyé à l'API.
     */
    private updateUrl(payload: FormationSearchPayload): void {
        history.pushState(null, '', window.location.pathname + this.buildQuery(payload));
    }

    /**
     * Construit la querystring (préfixée de « ? », vide si aucun filtre) reflétant le payload.
     * Partagée entre updateUrl() et la sortie de la route domaine.
     */
    private buildQuery(payload: FormationSearchPayload): string {
        const parts: string[] = [];

        if (payload.recherche)
            parts.push('recherche=' + encodeURIComponent(payload.recherche));

        if (payload.localisation)
            parts.push('localisation=' + encodeURIComponent(payload.localisation));

        // Filtres multi-valeurs : virgule littérale comme séparateur (ex: typologies=1,2,3)
        if (payload.domaines?.length)   parts.push('domaines='   + payload.domaines.join(','));
        if (payload.typologies?.length) parts.push('typologies=' + payload.typologies.join(','));
        if (payload.ccits?.length)      parts.push('ccits='      + payload.ccits.join(','));
        if (payload.niveaux?.length)    parts.push('niveaux='    + payload.niveaux.join(','));
        if (payload.criteres?.length)   parts.push('criteres='   + payload.criteres.join(','));

        if (payload.cpf)      parts.push('cpf=true');
        if (payload.page > 1) parts.push('page=' + payload.page);

        return parts.length ? '?' + parts.join('&') : '';
    }

    // ── En-tête propre à un domaine (CFB-519) ─────────────────────────

    /**
     * Vrai si l'en-tête serveur est propre à un domaine et que les domaines cochés ne sont
     * plus ceux pour lesquels il a été rendu : il ne correspond alors plus aux résultats.
     */
    private enteteDomainePerime(payload: FormationSearchPayload): boolean {
        // En-tête générique : le tag helper <form> rend l'attribut vide (valeur null) au lieu
        // de l'omettre ; un en-tête de domaine porte toujours au moins un id.
        const entete = this.dataset.domainesEntete;
        if (!entete) return false;

        const parId = (a: number, b: number) => a - b;
        const initiaux = entete.split(',').filter(Boolean).map(Number).sort(parId);
        const courants = [...(payload.domaines ?? [])].sort(parId);
        return initiaux.length !== courants.length
            || initiaux.some((id, i) => id !== courants[i]);
    }

    // ── Étiquettes des filtres actifs (CFB-519) ───────────────────────

    /**
     * Reconstruit les étiquettes depuis l'état courant du formulaire.
     * Un domaine parent coché garde les étiquettes de ses sous-domaines : sa croix retire tout
     * le domaine, celle d'un sous-domaine permet d'affiner sans ouvrir l'arbre (CFB-519).
     * Les libellés passent par textContent (aucune injection HTML).
     */
    private renderFiltresActifs(): void {
        const conteneur = this.filtresActifsEl;
        if (!conteneur) return;

        const etiquettes: { cle: string; libelle: string }[] = [];

        const recherche = this.inputSearch?.value.trim();
        if (recherche) etiquettes.push({ cle: 'recherche', libelle: `« ${recherche} »` });

        const localisation = this.querySelector<HTMLInputElement>('input[name="localisation"]')?.value.trim();
        if (localisation) etiquettes.push({ cle: 'localisation', libelle: localisation });

        this.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').forEach(input => {
            const label = this.querySelector(`label[for="${input.id}"]`);
            const libelle = (label?.querySelector('span') ?? label)?.textContent?.trim();
            if (libelle) etiquettes.push({ cle: '#' + input.id, libelle });
        });

        conteneur.replaceChildren();
        conteneur.hidden = etiquettes.length === 0;
        if (!etiquettes.length) return;

        for (const { cle, libelle } of etiquettes) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'filtre-actif';
            btn.dataset.retirer = cle;
            btn.setAttribute('aria-label', `Retirer le filtre ${libelle}`);
            btn.textContent = libelle;
            btn.insertAdjacentHTML('beforeend',
                '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">'
                + '<path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>');
            conteneur.appendChild(btn);
        }

        if (etiquettes.length > 1) {
            const toutEffacer = document.createElement('button');
            toutEffacer.type = 'button';
            toutEffacer.className = 'btn btn-link btn-sm p-0';
            toutEffacer.dataset.retirer = '*';
            toutEffacer.textContent = 'Tout effacer';
            conteneur.appendChild(toutEffacer);
        }
    }

    /**
     * Retire un filtre (ou tous avec « * ») puis relance la recherche.
     * Une case décochée émet un change, pour que FilterTree propage aux sous-domaines.
     */
    private retirerFiltre(cle: string): void {
        const tout = cle === '*';

        if ((tout || cle === 'recherche') && this.inputSearch) this.inputSearch.value = '';

        if (tout || cle === 'localisation') {
            const localisation = this.querySelector<HTMLInputElement>('input[name="localisation"]');
            if (localisation) localisation.value = '';
        }

        // Clé « #id » : la case du filtre ; « * » : toutes les cases.
        const selecteur = tout ? 'input[type="checkbox"]' : cle.startsWith('#') ? cle : null;
        const cases = selecteur ? Array.from(this.querySelectorAll<HTMLInputElement>(selecteur)) : [];
        cases.forEach(c => {
            c.indeterminate = false;
            if (!c.checked) return;
            c.checked = false;
            c.dispatchEvent(new Event('change', { bubbles: true }));
        });

        this.scheduleSearch(0);
    }

    // ── Fetch + injection ─────────────────────────────────────────────

    /**
     * Envoie le payload à l'endpoint Razor partial et injecte le HTML retourné
     * dans les trois zones cibles du DOM.
     *
     * Le HTML reçu est parsé via DOMParser pour extraire proprement les zones
     * #formations-count, #formations-results et #formations-pagination,
     * sans risquer d'exécuter des scripts parasites.
     *
     * Après injection :
     *  - bindPaginationLinks() re-branche les nouveaux liens de pagination,
     *  - updateUrl() synchronise l'URL du navigateur,
     *  - Sur mobile (< 992 px), scroll vers les résultats.
     *
     * @param page Numéro de page à charger (0-based).
     * @param isPagination Vrai si l'appel provient d'un clic sur la pagination
     *                     (le scroll est alors géré immédiatement par le handler de clic,
     *                     on n'ajoute donc pas de scroll post-injection ici).
     */
    private async fetchResults(page: number, isPagination = false): Promise<void> {
        const payload = this.buildPayload(page);
        this.setLoading(true);
        this.renderFiltresActifs();

        // Changement de domaine sous un en-tête de domaine : on recharge la liste de résultats
        // (rendu serveur complet, en-tête cohérent) plutôt que de rafraîchir en AJAX.
        if (this.enteteDomainePerime(payload)) {
            if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
            this.inFlight?.abort();
            window.location.assign(this.dataset.listeUrl + this.buildQuery(payload));
            return;
        }

        // Annule une éventuelle requête précédente encore en vol : si l'utilisateur
        // enchaîne les clics de pagination, seule la dernière demande aboutit.
        this.inFlight?.abort();
        const controller = new AbortController();
        this.inFlight = controller;

        try {
            const response = await fetch(FormationSearch.ENDPOINT, {
                method:  'POST',
                headers: {
                    'Content-Type':     'application/json',
                    'X-Requested-With': 'XMLHttpRequest', // identifie la requête comme AJAX côté serveur
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            if (!response.ok) throw new Error(`[FormationSearch] HTTP ${response.status}`);

            // Parsing du HTML partiel retourné par _ResultatsFormation.cshtml
            const html = await response.text();
            const doc  = new DOMParser().parseFromString(html, 'text/html');

            // Extraction des trois zones par leur ID (mêmes IDs que dans le partial Razor)
            const newCount   = doc.getElementById('formations-count');
            const newResults = doc.getElementById('formations-results');
            const newPagin   = doc.getElementById('formations-pagination');

            // Injection sélective : on ne remplace que l'innerHTML pour
            // conserver les attributs et les références des éléments parents.
            if (newCount   && this.countEl)   this.countEl.innerHTML   = newCount.innerHTML;
            if (newResults && this.resultsEl) this.resultsEl.innerHTML = newResults.innerHTML;
            if (newPagin   && this.paginEl)   this.paginEl.innerHTML   = newPagin.innerHTML;

            // Re-brancher les liens de pagination car ils viennent d'être recréés
            this.bindPaginationLinks();
            this.updateUrl(payload);

            // Scroll post-injection réservé au filtrage/recherche sur mobile.
            // (La pagination gère son propre scroll immédiat dès le clic, cf. bindPaginationLinks.)
            if (!isPagination && window.innerWidth < 992) {
                this.resultsEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } catch (err) {
            // Une requête annulée (nouveau clic) n'est pas une erreur : on sort sans rien logger
            // ni restaurer l'état de chargement (la requête plus récente s'en charge).
            if (err instanceof DOMException && err.name === 'AbortError') return;
            console.error('[FormationSearch] fetchResults error', err);
        } finally {
            // Restaure l'état visuel uniquement si cette requête est toujours la plus récente,
            // pour éviter de « déloader » alors qu'une requête suivante est encore en cours.
            if (this.inFlight === controller) {
                this.setLoading(false);
                this.inFlight = null;
            }
        }
    }

    // ── Loading state ─────────────────────────────────────────────────

    /**
     * Applique ou retire visuellement l'état de chargement sur la grille de résultats.
     * L'opacité réduite et le blocage des interactions signalent à l'utilisateur
     * qu'une requête est en cours, sans masquer complètement le contenu précédent.
     *
     * @param on `true` pour activer l'état de chargement, `false` pour le retirer.
     */
    private setLoading(on: boolean): void {
        if (!this.resultsEl) return;
        this.resultsEl.style.opacity       = on ? '0.4' : '1';
        this.resultsEl.style.pointerEvents = on ? 'none' : '';
    }

    // ── Pagination ────────────────────────────────────────────────────

    /**
     * Branche la navigation AJAX sur chaque lien de pagination présent dans #formations-pagination.
     *
     * Appelée :
     *  - une première fois dans connectedCallback() pour la pagination SSR initiale,
     *  - après chaque injection AJAX car les liens sont recréés dans le DOM.
     *
     * Technique du cloneNode pour éviter l'accumulation de listeners :
     * après une injection innerHTML, les anciens éléments sont détruits et recréés,
     * mais on clone quand même par précaution en cas d'appel multiple.
     * Le clone remplace l'original dans le DOM, garantissant exactement un listener par lien.
     *
     * Le numéro de page est extrait du paramètre `?page=N` de l'href (1-based côté Razor,
     * converti en 0-based pour l'API : page - 1).
     */
    private bindPaginationLinks(): void {
        if (!this.paginEl) return;
        this.paginEl.querySelectorAll<HTMLAnchorElement>('a.page-link[href]').forEach(link => {
            // Clone sans listener pour repartir d'un état propre
            const clone = link.cloneNode(true) as HTMLAnchorElement;
            link.replaceWith(clone);
            clone.addEventListener('click', (e: MouseEvent) => {
                e.preventDefault();
                // Extraction du numéro de page depuis l'URL du lien (ex: ?page=3)
                // _Pagination.cshtml génère pageParam = i (1-based), cohérent avec le serveur.
                // La page 1 pointe vers l'URL propre (sans ?page), d'où le repli sur '1'.
                const url  = new URL(clone.href, window.location.href);
                const page = parseInt(url.searchParams.get('page') ?? '1', 10);
                // Retour immédiat en haut du catalogue (tous écrans) : feedback instantané
                // même si la requête serveur est lente, puis chargement des résultats.
                this.scrollToCatalogueTop();
                this.fetchResults(page, true);
            });
        });
    }

    /**
     * Fait défiler la vue jusqu'en haut du catalogue (le <form> lui-même, qui contient
     * la barre de recherche, le compteur et la grille de résultats).
     * Appelé dès le clic sur une page pour un retour visuel immédiat.
     */
    private scrollToCatalogueTop(): void {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Corps de la requête POST envoyée à /api/recherche/formations/partial.
 * Doit rester synchronisé avec la classe C# `FormationSearchAjaxRequest`
 * dans CCIFormation.Front/Controllers/SearchController.cs.
 *
 * Conventions :
 *  - `null` signifie "pas de filtre sur ce critère" (le serveur l'ignore).
 *  - `domaines` contient des GUIDs (string) ; tous les autres filtres des slugs.
 *  - `page` est 0-based (le serveur multiplie par `PageSize` pour l'offset ES).
 */
interface FormationSearchPayload {
    /** Texte libre de la barre de recherche. */
    recherche:  string | null;
    /** Localisation (ville / CP) issue du champ « Où souhaitez-vous vous former ». */
    localisation: string | null;
    /** IDs des domaines/sous-domaines cochés. */
    domaines:   number[] | null;
    /** IDs des typologies de formation cochées. */
    typologies: number[] | null;
    /** IDs des CCIT cochées. */
    ccits:      number[] | null;
    /** IDs des niveaux d'entrée cochés. */
    niveaux:    number[] | null;
    /** IDs des critères libres cochés. */
    criteres:   number[] | null;
    /** `true` si le filtre CPF est actif, `null` sinon. */
    cpf:        boolean | null;
    /** Numéro de page demandé, 0-based. */
    page:       number;
    /** Id du domaine parent (route domaine parent) : déclenche le regroupement par sous-domaine. */
    domaineParentId: number | null;
}
